import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LucideAngularModule,
  Sparkles,
  User,
  BookOpen,
  ArrowRight,
  ChevronDown,
  Calculator,
  FlaskConical,
  BookText,
  Landmark,
  Globe,
  Languages,
  Palette,
  Heart,
  Code,
  Cpu,
  Briefcase,
  Wrench,
  Accessibility,
  MessageSquare,
  Smile,
  Church,
  GraduationCap,
  Presentation,
  Star,
  UserCircle,
  Users,
  Shield,
  MoreHorizontal,
  Home,
  Building2,
  School,
  Library,
  Monitor,
  BookHeart,
  Brain,
  HeartHandshake,
  type LucideIconData,
} from 'lucide-angular';
import { WizardDataService } from '../../../../common/wizard/wizard-data.service';

@Component({
  selector: 'app-step-profile',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule, RouterLink],
  templateUrl: './step-profile.html',
  styleUrl: './step-profile.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepProfileV2Component implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly wizardDataService = inject(WizardDataService);

  protected readonly isLoading$ = new BehaviorSubject<boolean>(false);
  protected readonly selectedGrades = signal<string[]>([]);
  protected readonly selectedSubjects = signal<string[]>([]);

  protected readonly profileForm: FormGroup;

  protected readonly icons = {
    Sparkles,
    User,
    BookOpen,
    ArrowRight,
    ChevronDown,
  };

  protected readonly roleIcons: Record<string, LucideIconData> = {
    'Teacher': UserCircle,
    'Educator': BookOpen,
    'Instructional Coach': Users,
    'Guardian or Homeschool Educator': Home,
    'District Administrator': Building2,
    'School Administrator': School,
    'Library or Media Specialist': Library,
    'Technology Administrator': Monitor,
    'Administrator': Shield,
    'Higher Education Professional': GraduationCap,
    'Social Worker or Psychologist': Brain,
    'Counselor': HeartHandshake,
    'Other': MoreHorizontal,
  };

  protected readonly subjectIcons: Record<string, LucideIconData> = {
    'All Core Subjects': BookOpen,
    'Math': Calculator,
    'Science/STEM': FlaskConical,
    'English/Language Arts': BookText,
    'History': Landmark,
    'Social Studies': Globe,
    'World Languages': Languages,
    'Art And/Or Music': Palette,
    'Health & Physical Education': Heart,
    'Computer Science': Code,
    'Technology and Engineering': Cpu,
    'Business': Briefcase,
    'Career and Technical Education': Wrench,
    'Special Education': Accessibility,
    'English as a New Language (ENL/ESL)': MessageSquare,
    'Social Emotional Learning': Smile,
    'Religion/Theology': Church,
    'College/University': GraduationCap,
    'Pre-Service Teacher Training': Presentation,
    'Electives': Star,
  };

  protected readonly grades: readonly string[] = [
    'Pre-K', 'K', '1st', '2nd', '3rd', '4th',
    '5th', '6th', '7th', '8th', '9th', '10th',
    '11th', '12th', 'Higher Ed',
  ];

  protected readonly subjects: readonly string[] = [
    'All Core Subjects',
    'Math',
    'Science/STEM',
    'English/Language Arts',
    'History',
    'Social Studies',
    'World Languages',
    'Art And/Or Music',
    'Health & Physical Education',
    'Computer Science',
    'Technology and Engineering',
    'Business',
    'Career and Technical Education',
    'Special Education',
    'English as a New Language (ENL/ESL)',
    'Social Emotional Learning',
    'Religion/Theology',
    'College/University',
    'Pre-Service Teacher Training',
    'Electives',
  ];

  protected readonly roles: readonly string[] = [
    'Teacher',
    'Educator',
    'Instructional Coach',
    'Guardian or Homeschool Educator',
    'District Administrator',
    'School Administrator',
    'Library or Media Specialist',
    'Technology Administrator',
    'Administrator',
    'Higher Education Professional',
    'Social Worker or Psychologist',
    'Counselor',
    'Other',
  ];

  protected readonly nextStepUrl = '/wizard/v2/school';

  constructor() {
    this.profileForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: [''],
      role: ['', [Validators.required]],
    });
  }

  async ngOnInit(): Promise<void> {
    // Load data from backend first
    const existingData = await this.wizardDataService.ensureLoaded();

    // Set wizard version
    this.wizardDataService.save({ wizardVersion: 'v2' });

    // Pre-populate form with loaded data
    if (existingData.firstName || existingData.lastName || existingData.role) {
      this.profileForm.patchValue({
        firstName: existingData.firstName || '',
        lastName: existingData.lastName || '',
        role: existingData.role || '',
      }, { emitEvent: false });
    }
    if (existingData.grades?.length) {
      this.selectedGrades.set(existingData.grades);
    }
    if (existingData.subjects?.length) {
      this.selectedSubjects.set(existingData.subjects);
    }

    // Auto-save on form changes (after form is populated)
    this.profileForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.wizardDataService.save({
          firstName: value.firstName,
          lastName: value.lastName,
          role: value.role,
        });
      });
  }

  protected isGradeSelected(grade: string): boolean {
    return this.selectedGrades().includes(grade);
  }

  protected toggleGrade(grade: string): void {
    this.selectedGrades.update((current) => {
      const updated = current.includes(grade)
        ? current.filter((g) => g !== grade)
        : [...current, grade];
      // Auto-save grades
      this.wizardDataService.save({ grades: updated });
      return updated;
    });
  }

  protected isSubjectSelected(subject: string): boolean {
    return this.selectedSubjects().includes(subject);
  }

  protected toggleSubject(subject: string): void {
    this.selectedSubjects.update((current) => {
      const updated = current.includes(subject)
        ? current.filter((s) => s !== subject)
        : [...current, subject];
      // Auto-save subjects
      this.wizardDataService.save({ subjects: updated });
      return updated;
    });
  }

  protected onSubmit(): void {
    if (this.profileForm.invalid || this.selectedGrades().length === 0 || this.selectedSubjects().length === 0) {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.isLoading$.next(true);

    this.isLoading$.next(false);
    this.router.navigate([this.nextStepUrl]);
  }

  protected selectRole(role: string): void {
    this.profileForm.get('role')?.setValue(role);
    this.profileForm.get('role')?.markAsTouched();
  }
  protected isRoleSelected(role: string): boolean {
    return this.profileForm.get('role')?.value === role;
  }
}


